import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { DocumentBreadcrumbItem, DocumentFolder } from '../../types';

const NOT_FOUND_REDIRECT_DELAY_MS = 1200;

interface UseDocumentNavigationOptions {
  folderId: string | undefined;
  breadcrumbItems: DocumentBreadcrumbItem[];
  isNotFoundError: boolean;
  onSelectionClear: () => void;
}

/**
 * Handles all router-level navigation within the document explorer:
 * navigating into folders, breadcrumb clicks, back-to-root, and
 * the delayed redirect when a folder returns a 404.
 */
export function useDocumentNavigation({
  folderId,
  breadcrumbItems,
  isNotFoundError,
  onSelectionClear,
}: UseDocumentNavigationOptions) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const notFoundTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handledNotFoundRef = useRef<string | null>(null);

  const navigateToRoot = useCallback(
    (replace = false) => {
      navigate({ to: '/documents', replace, state: { documentBreadcrumbs: undefined } });
    },
    [navigate]
  );

  const navigateToFolder = useCallback(
    (folder: DocumentFolder, nextBreadcrumbs: DocumentBreadcrumbItem[]) => {
      navigate({
        to: '/documents/folders/$folderId',
        params: { folderId: folder.id },
        state: { documentBreadcrumbs: nextBreadcrumbs },
      });
    },
    [navigate]
  );

  const navigateToBreadcrumbItem = useCallback(
    (item: DocumentBreadcrumbItem, index: number) => {
      onSelectionClear();
      navigate({
        to: '/documents/folders/$folderId',
        params: { folderId: item.id },
        state: { documentBreadcrumbs: breadcrumbItems.slice(0, index + 1) as DocumentBreadcrumbItem[] },
      });
    },
    [navigate, breadcrumbItems, onSelectionClear]
  );

  /** Navigates into a folder and clears any active file selection. */
  const handleFolderClick = useCallback(
    (folder: DocumentFolder) => {
      onSelectionClear();
      navigateToFolder(folder, [...breadcrumbItems, { id: folder.id, name: folder.name }] as DocumentBreadcrumbItem[]);
    },
    [breadcrumbItems, navigateToFolder, onSelectionClear]
  );

  // Show a toast and redirect to root when the requested folder no longer exists
  useEffect(() => {
    if (notFoundTimeoutRef.current) {
      clearTimeout(notFoundTimeoutRef.current);
      notFoundTimeoutRef.current = null;
    }

    const shouldRedirect = folderId && isNotFoundError;
    if (!shouldRedirect) {
      handledNotFoundRef.current = null;
      return;
    }

    const alreadyHandledThisFolder = handledNotFoundRef.current === folderId;
    if (alreadyHandledThisFolder) return;

    handledNotFoundRef.current = folderId;
    toast.error(t('documents.states.folderNotFoundToast'));
    notFoundTimeoutRef.current = setTimeout(() => navigateToRoot(true), NOT_FOUND_REDIRECT_DELAY_MS);

    return () => {
      if (notFoundTimeoutRef.current) {
        clearTimeout(notFoundTimeoutRef.current);
        notFoundTimeoutRef.current = null;
      }
    };
  }, [folderId, isNotFoundError, navigateToRoot, t]);

  return { navigateToRoot, navigateToFolder, navigateToBreadcrumbItem, handleFolderClick };
}
